/*
 * Pagination module for sortable lists
 */

define([
  "marionette",
  "backbone",
  "messaging/messenger",
  "modules/shared/sortableList/sortableListPagination/sortableListPaginationView",
], function (Marionette, Backbone, Messenger, SortableListPaginationView) {
  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "sortableListPagination";
      this.region = options.region;
      this.recordsPerPage = options.recordsPerPage;
      this.namespace = options.namespace;
      this.namespacePrefix = this.namespace ? this.namespace + ":" : "";
    },
    show: function (collection) {
      this.collection = collection;

      this.model = new Backbone.Model({
        pageCount: 0,
        currentPage: 1,
        numberOfRecords: 0,
        startRecord: 0,
        endRecord: 0,
        recordsPerPage: this.recordsPerPage,
      });

      this.view = new SortableListPaginationView.sortableListPaginationView({ model: this.model });
      // calculate number of pages if collection has already been loaded
      if (this.collection.checkSynced()) {
        this._calculateNumberOfPages().then();
      }

      this._initializeEventHandlers();
      this.showRegion(this.view);
    },
    paginationFilter: function () {
      return (modelIndex) => {
        const page = Math.floor(modelIndex / this.recordsPerPage) + 1;
        return page === this.model.get("currentPage");
      };
    },
    setCurrentPage: function (currentPage, { forceUpdate = false } = {}) {
      if (
        forceUpdate ||
        (currentPage != this.model.get("currentPage") &&
          currentPage <= this.model.get("pageCount") &&
          currentPage >= 1)
      ) {
        this.model.set("currentPage", currentPage);
        this._calculateRecordRange();
        Messenger.vent.trigger(
          this.namespacePrefix + "change:page",
          currentPage,
          this.recordsPerPage,
        );
      }
    },
    _handleFilter: function () {
      this.model.set("currentPage", 1);
      this._calculateNumberOfPages();
      this.view.render();
    },
    _handleCollectionChange: async function () {
      if (this.collection.checkSynced()) {
        await this._calculateNumberOfPages();
        let currentPage = this.model.get("currentPage");
        let forceUpdate = false;
        // If the current page is bigger than the number of pages (e.g. if we delete a lot of rows
        // and were on the last page), then move the current page to the last page
        if (currentPage > this.model.get("pageCount")) {
          currentPage = Math.max(this.model.get("pageCount"), 1);
          forceUpdate = true;
        }
        this.setCurrentPage(currentPage, { forceUpdate });
      }
    },
    _initializeEventHandlers: function () {
      //update pagination when we add or remove a model, but only if the model is not being fetched
      this.listenTo(
        this.collection,
        "add remove removeMultiple reset sync",
        this._handleCollectionChange,
      );
      //update pagination when we filter the collection
      this.listenTo(this.collection, "filter", this._handleFilter);
      this.view.on("next", this._handleNext, this);
      this.view.on("back", this._handleBack, this);
      this.view.on("change:page:size", this._changePageSize, this);
    },
    _handleNext: function () {
      this.setCurrentPage(this.model.get("currentPage") + 1);
    },
    _handleBack: function () {
      this.setCurrentPage(this.model.get("currentPage") - 1);
    },
    _changePageSize: async function (pageSize) {
      this.recordsPerPage = +pageSize;
      const currentPage = 1;
      this.model.set({
        recordsPerPage: this.recordsPerPage,
        currentPage,
      });
      await this._calculateNumberOfPages();
      this.setCurrentPage(1, { forceUpdate: true });
    },
    _calculateNumberOfPages: async function () {
      //calculate number of pages
      let numberOfRecords = 0;
      let itemCount = 0;
      let pageCount = 0;
      for (let i = 0; i < this.collection.size(); i++) {
        if (!this.collection.at(i).filteredOut) {
          itemCount++;
          numberOfRecords++;
        }
        if (itemCount === this.recordsPerPage) {
          itemCount = 0;
          pageCount++;
        }
      }
      if (itemCount > 0) {
        pageCount++;
      }

      this.model.set({
        pageCount,
        numberOfRecords,
      });
      this._calculateRecordRange();
    },
    _calculateRecordRange: function () {
      const currentPage = this.model.get("currentPage");
      this.model.set("startRecord", 1 + (currentPage - 1) * this.recordsPerPage);
      this.model.set(
        "endRecord",
        Math.min(currentPage * this.recordsPerPage, this.model.get("numberOfRecords")),
      );
    },
  });
});
